"use client";

import { cn } from "@gettactic/helpers";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface ToggleGroupItem {
  value: string;
  /**
   * Label rendered inside the toggle button. Typed as ReactNode so callers
   * can compose richer content (e.g. label + count badge) rather than only
   * plain strings.
   */
  label: React.ReactNode;
}

interface ToggleGroupProps {
  items: ToggleGroupItem[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  /**
   * When true, the toggle group fills its container and each item gets an
   * equal share of the available width (`flex-1`). When false (the default)
   * the group sits inline at its intrinsic width.
   */
  fullWidth?: boolean;
}

export const ToggleGroup: React.FC<ToggleGroupProps> = ({
  items,
  value,
  onValueChange,
  className,
  fullWidth = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliderStyle, setSliderStyle] = useState<React.CSSProperties>({});

  const updateSlider = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const activeIndex = items.findIndex((item) => item.value === value);
    if (activeIndex === -1) return;

    const buttons = container.querySelectorAll<HTMLButtonElement>(
      "[data-toggle-item]"
    );
    const activeButton = buttons[activeIndex];
    if (!activeButton) return;

    setSliderStyle({
      left: `${activeButton.offsetLeft}px`,
      width: `${activeButton.offsetWidth}px`
    });
  }, [items, value]);

  useEffect(() => {
    updateSlider();
  }, [updateSlider]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative items-center rounded-full bg-neutral-100 p-1",
        fullWidth ? "flex w-full" : "inline-flex",
        className
      )}
    >
      {/* Sliding background */}
      <div
        className="absolute top-1 bottom-1 rounded-full bg-white shadow-sm transition-all duration-200 ease-in-out"
        style={sliderStyle}
      />

      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          data-toggle-item
          onClick={() => onValueChange(item.value)}
          className={cn(
            "relative z-10 cursor-pointer rounded-full px-4 py-1.5 font-medium text-sm transition-colors",
            fullWidth && "flex-1",
            value === item.value
              ? "text-neutral-900"
              : "text-neutral-500 hover:text-neutral-700"
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};
